import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Crown, Check, Zap, Star, Shield, Clock, Wallet, QrCode, Copy, ExternalLink, Download, Video, Music, FileText, File, Link as LinkIcon, Lock, Share2, Package } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { copyToClipboard } from '../../utils/clipboard';
import ShareButton from '../../components/ShareButton';

const FILE_TYPE_ICONS = {
  url: LinkIcon,
  mp4: Video,
  mp3: Music,
  pdf: FileText,
  doc: File,
  image: File
};

const ActivationPacks = () => {
  const { user, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [products, setProducts] = useState([]);
  const [activationStatus, setActivationStatus] = useState(null);
  const [activePack, setActivePack] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('manual');
  const [showMyPack, setShowMyPack] = useState(false);
  const [unlockedProducts, setUnlockedProducts] = useState([]);
  const [walletBalances, setWalletBalances] = useState({ cash_wallet: 0, commission_wallet: 0, promotion_wallet: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch wallet balances directly (same as Wallets page)
  const fetchWalletBalances = async () => {
    try {
      const res = await api.get('/wallet/balances');
      setWalletBalances(res.data);
      return res.data;
    } catch (err) {
      console.error('Failed to fetch wallet balances:', err);
      return null;
    }
  };

  const fetchData = async () => {
    try {
      const [packsRes, statusRes, settingsRes, productsRes, balancesRes] = await Promise.all([
        api.get('/activation/packs'),
        api.get('/activation/status'),
        api.get('/settings'),
        api.get('/activation/available-products'),
        api.get('/wallet/balances')
      ]);
      setPacks(packsRes.data);
      setActivationStatus(statusRes.data);
      setSettings(settingsRes.data);
      setProducts(productsRes.data.products || []);
      setWalletBalances(balancesRes.data);
      
      // If activated, find and set the active pack with files
      if (statusRes.data?.is_activated && statusRes.data?.pack_id) {
        const active = packsRes.data.find(p => p.id === statusRes.data.pack_id);
        setActivePack(active);
      }
      
      // Fetch unlocked products if activated
      if (statusRes.data?.unlocked_products) {
        setUnlockedProducts(statusRes.data.unlocked_products);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (pack) => {
    // Refresh wallet balances to get latest data
    await fetchWalletBalances();
    setSelectedPack(pack);
    setSelectedProduct(null); // Reset product selection
  };

  const copyUPI = async () => {
    if (settings?.manual_payment_upi) {
      const success = await copyToClipboard(settings.manual_payment_upi);
      if (success) toast.success('UPI ID copied!');
      else toast.error('Failed to copy');
    }
  };

  const confirmPurchase = async () => {
    if (!selectedPack) return;
    
    // Validate product selection
    if (!selectedProduct) {
      toast.error('Please select a product to unlock');
      return;
    }
    
    setPurchasing(true);
    try {
      const response = await api.post('/activation/purchase', {
        pack_id: selectedPack.id,
        payment_method: paymentMethod,
        selected_product_id: selectedProduct.id
      });
      
      toast.success(response.data.message);
      
      // Play notification sound
      try {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(() => {});
      } catch (e) {
        // Sound play failed - ignore
      }
      
      // If pending, show success message and stay on page
      if (response.data.status === 'pending') {
        toast.success('Payment request sent! Admin will verify and activate your account.', { duration: 5000 });
      }
      
      // Show unlocked product info
      if (response.data.unlocked_product) {
        toast.success(`Product unlocked: ${response.data.unlocked_product}`, { duration: 4000 });
      }
      
      setSelectedPack(null);
      setSelectedProduct(null);
      fetchData();
      if (fetchUser) fetchUser();
      
      // Redirect to dashboard after successful activation (only for wallet payments)
      if (response.data.status === 'active') {
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  // Timer component for days remaining
  const formatTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return { days, hours, minutes, seconds, expired: false };
  };

  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if (activationStatus?.is_activated && activationStatus?.expires_at) {
      const timer = setInterval(() => {
        setTimeRemaining(formatTimeRemaining(activationStatus.expires_at));
      }, 1000);
      
      setTimeRemaining(formatTimeRemaining(activationStatus.expires_at));
      
      return () => clearInterval(timer);
    }
  }, [activationStatus]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="activation-packs-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full mb-4">
            <Zap className="w-5 h-5" />
            <span className="font-semibold">Affiliate Network Activation</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Activate Your <span className="text-primary">Freedom</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Unlock unlimited earning potential with our activation packs. 
            Start building your network and earn commissions today!
          </p>
        </div>

        {/* Current Status Banner with Timer */}
        {activationStatus && (
          <div className={`mb-8 p-6 rounded-2xl border-2 ${
            activationStatus.is_activated 
              ? 'bg-green-500/10 border-green-500/30' 
              : activationStatus.status === 'pending'
                ? 'bg-yellow-500/10 border-yellow-500/30'
                : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  activationStatus.is_activated ? 'bg-green-500/20' : 'bg-yellow-500/20'
                }`}>
                  {activationStatus.is_activated ? (
                    <Check className="w-6 h-6 text-green-500" />
                  ) : (
                    <Clock className="w-6 h-6 text-yellow-500" />
                  )}
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${
                    activationStatus.is_activated ? 'text-green-500' : 'text-yellow-500'
                  }`}>
                    {activationStatus.is_activated ? 'Account Activated' : 
                     activationStatus.status === 'pending' ? 'Activation Pending Approval' : 'Activation Required'}
                  </h3>
                  <p className="text-muted-foreground">{activationStatus.message}</p>
                </div>
              </div>
              
              {/* Timer Display - Days:Hours:Minutes:Seconds */}
              {activationStatus.is_activated && timeRemaining && !timeRemaining.expired && (
                <div className="text-center">
                  <div className="bg-white/10 px-6 py-3 rounded-lg inline-block">
                    <p className="text-3xl font-bold text-green-500 font-mono tracking-wider">
                      {String(timeRemaining.days).padStart(2, '0')}:{String(timeRemaining.hours).padStart(2, '0')}:{String(timeRemaining.minutes).padStart(2, '0')}:{String(timeRemaining.seconds).padStart(2, '0')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Days : Hours : Minutes : Seconds</p>
                  </div>
                </div>
              )}
              
              {/* View Pack Files Button */}
              {activationStatus.is_activated && activePack?.files?.length > 0 && (
                <button
                  onClick={() => setShowMyPack(true)}
                  className="btn-primary flex items-center gap-2"
                  data-testid="view-pack-files-btn"
                >
                  <Download className="w-5 h-5" />
                  View Pack Files
                </button>
              )}
            </div>
          </div>
        )}

        {/* My Pack Files Section - Only visible when activated */}
        {activationStatus?.is_activated && activePack && (
          <div className="mb-8 glass-card p-6 border-2 border-green-500/30" data-testid="my-pack-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Crown className="w-6 h-6 text-primary" />
                My Activated Pack: {activePack.name}
              </h2>
              {activePack.files?.length > 0 && (
                <span className="text-sm bg-green-500/20 text-green-500 px-3 py-1 rounded-full">
                  {activePack.files.length} Files Available
                </span>
              )}
            </div>
            
            {activePack.files && activePack.files.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activePack.files.map((file, idx) => {
                  const Icon = FILE_TYPE_ICONS[file.file_type] || File;
                  return (
                    <a
                      key={idx}
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 hover:border-primary/50 transition-all group"
                      data-testid={`pack-file-${idx}`}
                    >
                      <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary/30">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground uppercase">{file.file_type}</p>
                      </div>
                      <Download className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                    </a>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">
                No downloadable files in this pack. Contact admin for access.
              </p>
            )}
          </div>
        )}

        {/* Pending Approval Notice */}
        {activationStatus?.status === 'pending' && (
          <div className="mb-8 glass-card p-6 border-2 border-yellow-500/30">
            <div className="flex items-center gap-3">
              <Lock className="w-8 h-8 text-yellow-500" />
              <div>
                <h3 className="font-bold text-yellow-500">Pack Files Locked</h3>
                <p className="text-muted-foreground">
                  Your pack files will be available once admin approves your activation request.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Wallet Balance Info */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="glass-card p-4 flex items-center gap-3">
            <Wallet className="w-8 h-8 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Cash Wallet</p>
              <p className="text-xl font-bold text-primary">₹{walletBalances?.cash_wallet?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
          <div className="glass-card p-4 flex items-center gap-3">
            <Wallet className="w-8 h-8 text-accent" />
            <div>
              <p className="text-sm text-muted-foreground">Commission Wallet</p>
              <p className="text-xl font-bold text-accent">₹{walletBalances?.commission_wallet?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>

        {/* Activation Packs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packs.map((pack) => (
            <div
              key={pack.id}
              className={`relative glass-card p-8 border-2 transition-all hover:scale-105 ${
                pack.is_featured 
                  ? 'border-primary shadow-lg shadow-primary/20' 
                  : 'border-white/10'
              }`}
              data-testid={`pack-${pack.id}`}
            >
              {pack.is_featured && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                    <Star className="w-4 h-4" />
                    RECOMMENDED
                  </div>
                </div>
              )}
              
              {/* Share Button */}
              <div className="absolute top-4 right-4">
                <ShareButton
                  type="activation"
                  itemId={pack.id}
                  itemName={pack.name}
                />
              </div>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">{pack.name}</h3>
                <p className="text-muted-foreground text-sm">{pack.description}</p>
              </div>

              <div className="text-center mb-6">
                <p className="text-5xl font-bold text-primary">₹{pack.price}</p>
                <p className="text-muted-foreground">for {pack.duration_days} days</p>
              </div>

              {/* Features List */}
              <ul className="space-y-3 mb-8">
                {pack.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-green-500" />
                    </div>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handlePurchase(pack)}
                disabled={activationStatus?.is_activated}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                  activationStatus?.is_activated
                    ? 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
                    : pack.is_featured
                      ? 'btn-primary'
                      : 'bg-white/10 hover:bg-white/20'
                }`}
                data-testid={`buy-pack-${pack.id}`}
              >
                {activationStatus?.is_activated ? 'Already Activated' : 'Activate Now'}
              </button>
            </div>
          ))}
        </div>

        {packs.length === 0 && (
          <div className="text-center py-12">
            <Shield className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Activation Packs Available</h3>
            <p className="text-muted-foreground">Please check back later or contact admin.</p>
          </div>
        )}

        {/* Purchase Modal */}
        {selectedPack && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="purchase-modal">
              <h2 className="text-2xl font-bold mb-2">Confirm Activation</h2>
              <p className="text-muted-foreground mb-6">
                You are purchasing <span className="text-primary font-bold">{selectedPack.name}</span>
              </p>

              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span>Pack Price</span>
                  <span className="font-bold">₹{selectedPack.price}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span>Duration</span>
                  <span className="font-bold">{selectedPack.duration_days} Days</span>
                </div>
                <hr className="border-white/10 my-2" />
                <div className="flex justify-between items-center">
                  <span className="font-bold">Total</span>
                  <span className="text-2xl font-bold text-primary">₹{selectedPack.price}</span>
                </div>
              </div>

              {/* Product Selection - Required */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4 text-primary" />
                  Select Product to Unlock <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  Choose ONE product that will be unlocked upon activation approval
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                  {products.map((product) => (
                    <label 
                      key={product.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedProduct?.id === product.id 
                          ? 'border-green-500 bg-green-500/10' 
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="radio"
                        name="product"
                        value={product.id}
                        checked={selectedProduct?.id === product.id}
                        onChange={() => setSelectedProduct(product)}
                        className="w-4 h-4"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{product.name}</p>
                        <p className="text-xs text-muted-foreground">₹{product.price}</p>
                      </div>
                      {product.images?.[0] && (
                        <img 
                          src={product.images[0]} 
                          alt={product.name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      )}
                    </label>
                  ))}
                </div>
                {selectedProduct && (
                  <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-500 font-medium">
                      ✓ Selected: {selectedProduct.name}
                    </p>
                  </div>
                )}
                {products.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">
                    No products available for selection
                  </p>
                )}
              </div>

              {/* Payment Method Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3">Select Payment Method</label>
                <div className="space-y-3">
                  <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    paymentMethod === 'manual' ? 'border-primary bg-primary/10' : 'border-white/10'
                  }`}>
                    <input
                      type="radio"
                      name="payment"
                      value="manual"
                      checked={paymentMethod === 'manual'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-5 h-5"
                    />
                    <div className="flex-1">
                      <p className="font-semibold">Manual Payment (UPI/QR)</p>
                      <p className="text-sm text-muted-foreground">Pay via UPI and wait for admin approval</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    paymentMethod === 'cash_wallet' ? 'border-primary bg-primary/10' : 'border-white/10'
                  }`}>
                    <input
                      type="radio"
                      name="payment"
                      value="cash_wallet"
                      checked={paymentMethod === 'cash_wallet'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-5 h-5"
                    />
                    <div className="flex-1">
                      <p className="font-semibold">100% Cash Wallet</p>
                      <p className="text-sm text-muted-foreground">Pay ₹{selectedPack.price} from Cash Wallet</p>
                    </div>
                    <span className="text-primary font-bold">₹{walletBalances?.cash_wallet?.toFixed(2)}</span>
                  </label>

                  <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    paymentMethod === 'mixed_wallet' ? 'border-primary bg-primary/10' : 'border-white/10'
                  }`}>
                    <input
                      type="radio"
                      name="payment"
                      value="mixed_wallet"
                      checked={paymentMethod === 'mixed_wallet'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-5 h-5"
                    />
                    <div className="flex-1">
                      <p className="font-semibold">50/50 Mixed Wallet</p>
                      <p className="text-sm text-muted-foreground">
                        ₹{(selectedPack.price / 2).toFixed(2)} Cash + ₹{(selectedPack.price / 2).toFixed(2)} Commission
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* QR Code and UPI for Manual Payment */}
              {paymentMethod === 'manual' && (
                <div className="mb-6 p-4 bg-white/5 rounded-xl border border-primary/30">
                  <div className="flex items-center gap-2 mb-4">
                    <QrCode className="w-5 h-5 text-primary" />
                    <h3 className="font-bold">Pay via UPI</h3>
                  </div>
                  
                  {settings?.manual_payment_qr && (
                    <div className="flex justify-center mb-4">
                      <img 
                        src={settings.manual_payment_qr} 
                        alt="Payment QR Code" 
                        className="w-48 h-48 rounded-xl border-2 border-white/20"
                        data-testid="payment-qr-code"
                      />
                    </div>
                  )}
                  
                  {settings?.manual_payment_upi && (
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-sm text-muted-foreground mb-1">UPI ID</p>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-mono font-bold text-primary" data-testid="payment-upi-id">
                          {settings.manual_payment_upi}
                        </p>
                        <button 
                          onClick={copyUPI}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-sm text-yellow-500 mt-4 text-center">
                    After payment, click "Submit Request" below. Admin will verify and activate your account.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={confirmPurchase}
                  disabled={purchasing}
                  className="flex-1 btn-primary disabled:opacity-50"
                  data-testid="confirm-purchase-btn"
                >
                  {purchasing ? 'Processing...' : paymentMethod === 'manual' ? 'Submit Request' : 'Confirm & Activate'}
                </button>
                <button
                  onClick={() => setSelectedPack(null)}
                  className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivationPacks;
